/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { getAVM1Object, wrapAVM1NativeClass } from './AVM1Utils';
import { notImplemented } from '@awayfl/swf-loader';
import { AVM1MovieClip } from './AVM1MovieClip';
import { ClipEventMappings, EventsListForButton } from './AVM1EventHandler';
var AVM1ButtonAction = /** @class */ (function () {
    function AVM1ButtonAction() {
    }
    return AVM1ButtonAction;
}());
/**
 * Key codes below 32 aren't interpreted as char codes, but are mapped to specific buttons instead.
 * This array uses the key code as the index and KeyboardEvent.keyCode values matching the
 * specific keys as the value.
 * @type {number[]}
 */
var AVM1KeyCodeMap = [-1, 37, 39, 36, 35, 45, 46, -1, 8, -1, -1, -1, -1, 13, 38, 40, 33, 34, 9, 27];
var buttonActionsMap = {
    1: ClipEventMappings[8192 /* AVM1ClipEvents.RollOver */],
    2: ClipEventMappings[16384 /* AVM1ClipEvents.RollOut */],
    4: ClipEventMappings[1024 /* AVM1ClipEvents.Press */],
    8: ClipEventMappings[2048 /* AVM1ClipEvents.Release */],
    64: ClipEventMappings[4096 /* AVM1ClipEvents.ReleaseOutside */],
    160: ClipEventMappings[32768 /* AVM1ClipEvents.DragOver */],
    272: ClipEventMappings[65536 /* AVM1ClipEvents.DragOut */]
};
var AVM1Button = /** @class */ (function (_super) {
    __extends(AVM1Button, _super);
    function AVM1Button() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    AVM1Button.createAVM1Class = function (context) {
        return wrapAVM1NativeClass(context, true, AVM1Button, [], ['$version#', '_alpha#', 'getAwayJSID', 'attachAudio', 'attachBitmap', 'attachMovie',
            'beginFill', 'beginBitmapFill', 'beginGradientFill', 'blendMode#',
            'cacheAsBitmap#', '_callFrame', 'clear', 'createEmptyMovieClip',
            'createTextField', '_currentframe#', 'curveTo', '_droptarget#',
            'duplicateMovieClip', 'enabled#', 'endFill', 'filters#', '_framesloaded#',
            '_focusrect#', 'forceSmoothing#', 'getBounds',
            'getBytesLoaded', 'getBytesTotal', 'getDepth', 'getInstanceAtDepth',
            'getNextHighestDepth', 'getRect', 'getSWFVersion', 'getTextSnapshot',
            'getURL', 'globalToLocal', 'gotoAndPlay', 'gotoAndStop', '_height#',
            '_highquality#', 'hitArea#', 'hitTest', 'lineGradientStyle', 'lineStyle',
            'lineTo', 'loadMovie', 'loadVariables', 'localToGlobal', '_lockroot#',
            'menu#', 'moveTo', '_name#', 'nextFrame', 'opaqueBackground#', '_parent#',
            'play', 'prevFrame', '_quality#', 'removeMovieClip', '_root#', '_rotation#',
            'scale9Grid#', 'scrollRect#', 'setMask', '_soundbuftime#', 'startDrag',
            'stop', 'stopDrag', 'swapDepths', 'tabChildren#', 'tabEnabled#', 'tabIndex#',
            '_target#', '_totalframes#', 'trackAsMenu#', 'toString',
            'unloadMovie', '_url#', 'useHandCursor#', '_visible#', '_width#',
            '_x#', '_xmouse#', '_xscale#', '_y#', '_ymouse#', '_yscale#']);
    };
    AVM1Button.prototype.freeFromScript = function () {
        _super.prototype.freeFromScript.call(this);
        // Button enabled by default
        this.adaptee.mouseEnabled = true;
        this.adaptee.mouseChildren = false;
    };
    AVM1Button.prototype._updateMouseEnabled = function (event, enabled) {
        if (this.adaptee.isAVMScene || !event.isMouse) {
            return;
        }
        this.adaptee.mouseEnabled = true;
        this.adaptee.mouseChildren = false;
        if (enabled) {
            this._mouseListenerCount++;
            if (event.isButton) {
                this._mouseButtonListenerCount++;
                this.adaptee.buttonMode = true;
            }
        }
        else {
            this._mouseListenerCount--;
            if (this._mouseListenerCount <= 0) {
                this._mouseListenerCount = 0;
            }
            if (event.isButton) {
                this._mouseButtonListenerCount--;
                if (this._mouseButtonListenerCount <= 0) {
                    this._mouseButtonListenerCount = 0;
                    this.adaptee.buttonMode = false;
                }
            }
        }
    };
    AVM1Button.prototype.initAVM1SymbolInstance = function (context, awayObject) {
        _super.prototype.initAVM1SymbolInstance.call(this, context, awayObject);
        this._requiredListeners = Object.create(null);
        this._actions = this.adaptee.timeline.avm1ButtonActions;
        var action = null;
        var boundListener = null;
        var foundValidAction = false;
        var actionsLength = this._actions.length;
        for (var i = 0; i < actionsLength; i++) {
            action = this._actions[i];
            if (!action.actionsBlock) {
                action.actionsBlock = context.actionsDataFactory.createActionsData(action.actionsData, 's' + this.adaptee.id + 'e' + i);
            }
            if (action.keyCode) {
                //requiredListeners['keyDown'] = this._keyDownHandler.bind(this);
                //continue;
            }
            if (action.stateTransitionFlags != 0) {
                boundListener = this._mouseEventHandler.bind(this, action.stateTransitionFlags);
                foundValidAction = false;
                for (var key in buttonActionsMap) {
                    if (action.stateTransitionFlags & parseInt(key)) {
                        foundValidAction = true;
                        //console.log("added event for name:",buttonActionsMap[key].eventName)
                        this._requiredListeners[buttonActionsMap[key].eventName] = {
                            handler: buttonActionsMap[key],
                            boundListener: boundListener
                        };
                    }
                }
                if (!foundValidAction) {
                    console.warn('unknown button event flag', action.stateTransitionFlags);
                }
            }
        }
        this.adaptee.addButtonListeners();
        this._initEventsHandlers();
        this._addListeners();
    };
    AVM1Button.prototype.initAdapter = function () {
        _super.prototype.initAdapter.call(this);
        this._addListeners();
    };
    AVM1Button.prototype.stop = function () {
        return this.adaptee.stop();
    };
    AVM1Button.prototype.getEnabled = function () {
        return this.enabled;
    };
    AVM1Button.prototype.setEnabled = function (value) {
        this.adaptee.buttonEnabled = value;
        this.adaptee.mouseEnabled = value;
        if (value == this.enabled)
            return;
        this.enabled = value;
        this.setEnabledListener(value);
    };
    AVM1Button.prototype.getTrackAsMenu = function () {
        notImplemented('AVM1Textfield.getTrackAsMenu');
        return false; //getAwayObjectOrTemplate(this).trackAsMenu;
    };
    AVM1Button.prototype.setTrackAsMenu = function (value) {
        notImplemented('AVM1Textfield.setTrackAsMenu');
        //getAwayObjectOrTemplate(this).trackAsMenu = alToBoolean(this.context, value);
    };
    AVM1Button.prototype._addListeners = function () {
        for (var type in this._requiredListeners) {
            this._eventHandlers[type] = this._requiredListeners[type].handler;
            this._eventsListeners[type] = this._requiredListeners[type].boundListener;
        }
        this.setEnabledListener(this.enabled);
    };
    AVM1Button.prototype._removeListeners = function () {
        var target = null;
        for (var type in this._requiredListeners) {
            //var target: EventDispatcher = type === 'keyDown' ?	(<any>this.adaptee).stage :	this.adaptee;
            target = this.adaptee;
            target.removeEventListener(type, this._requiredListeners[type].boundListener);
        }
    };
    AVM1Button.prototype._keyDownHandler = function (event) {
        var actions = this._actions;
        var actionsLength = actions.length;
        for (var i = 0; i < actionsLength; i++) {
            var action = actions[i];
            if (!action.keyCode) {
                continue;
            }
            if ((action.keyCode < 32 &&
                AVM1KeyCodeMap[action.keyCode] === event.axGetPublicProperty('keyCode')) ||
                action.keyCode === event.axGetPublicProperty('charCode')) {
                this._runAction(action);
            }
        }
    };
    AVM1Button.prototype._mouseEventHandler = function (type) {
        var actions = this._actions;
        var actionsLength = actions.length;
        for (var i = 0; i < actionsLength; i++) {
            var action = actions[i];
            if (action.stateTransitionFlags === type) {
                this._runAction(action);
            }
        }
    };
    AVM1Button.prototype._runAction = function (action) {
        var avm1Context = this._avm1Context; // (<LoaderInfo>this.adaptee.loaderInfo)._avm1Context;
        if (this.adaptee.parent) {
            avm1Context.executeActions(action.actionsBlock, getAVM1Object(this.adaptee.parent, this.context));
        }
    };
    AVM1Button.prototype._initEventsHandlers = function () {
        this.bindEvents(EventsListForButton);
    };
    return AVM1Button;
}(AVM1MovieClip));
export { AVM1Button };
